import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { EGIconButton } from "../../../../../../MainComponents/Button/IconButton/IconButton";
import { useReportContext } from "../../../../../ReportContext/useReportContext";
import { useTranslation } from "react-i18next";
import { EGButton } from "../../../../../../MainComponents/Button/EGButton/EGButton";
import { EGInput } from "../../../../../../MainComponents/Input/EGInput";
import { useSnackbar } from "notistack";
import {
  updateCompensations,
  updateSanctions,
} from "../../../../../../../apis";
import EGDatePicker from "../../../../../../MainComponents/DatePicker/EGDatePicker";
import dayjs from "dayjs";
import { useReportId } from "../../../../../../../context/report/ReportContext";
import { CatalogueMultiSelect } from "../../../../../../MainComponents/Catalogues/CatalogueMultiSelect";
import { useActionsCatalogue } from "../../../../../../../hooks/useActionsCatalogue";
import {
  ACTION_TYPES,
  buildAction,
  findAlreadyRegistered,
  getActionPath,
  getResolvedProposalKeys,
  getSavedProposalStates,
  normalizeSavedAnother,
  splitByOrigin,
  splitProposals,
} from "../../../../../../../helpers/correctiveActions";
import { isMultipleSanctionsEnabled } from "../../../../../../../config/features/multipleSanctions";
import { InvestigationProposals } from "../InvestigationProposals/InvestigationProposals";

export const EditSanctionsCompensationsForm = ({
  content,
  hdlCloseDialog,
  reloadData,
  reportRender,
  type,
  investigationList = [],
  // Registros ya guardados de este tipo; los de la misma persona son de otros equipos.
  records = [],
}) => {
  // Custom Hooks
  const id = useReportId();
  const [t] = useTranslation("report");
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { involvedType, sanctionsOptions, benefitOptions } = useReportContext();
  const isSanctions = type === "sanctions";

  // Mismo origen de opciones que la conclusion de la investigacion.
  const { catalogue, isOwn, rootPath, isLoading: isCatalogueLoading } =
    useActionsCatalogue(isSanctions ? ACTION_TYPES.SANCTION : null);

  // El registro guardado mezcla lo que salio de una investigacion con lo que
  // capturo el resolutor. Se separan para editar cada cosa donde corresponde:
  // las propuestas en su bloque y lo propio en el selector de catalogo. Si no,
  // al guardar se duplicarian los renglones.
  const savedActions = (
    content[isSanctions ? "sanctions" : "benefits"] || []
  ).map((item) => ({
    ...(typeof item === "object" && item ? item : {}),
    ...buildAction({
      path: getActionPath(item),
      label: typeof item === "string" ? "" : item?.label || "",
      locales: item?.locales || {},
    }),
  }));

  const savedAnother = normalizeSavedAnother(content.another);

  // Estado con el que quedo cada propuesta la vez anterior, para que el bloque
  // de investigaciones vuelva a abrirse como se dejo.
  const savedStates = getSavedProposalStates({
    actions: savedActions,
    another: savedAnother,
    discarded: content.discarded,
  });

  const multipleEnabled = isMultipleSanctionsEnabled();
  const otherRecords = multipleEnabled
    ? (records || []).filter(
        (record) => record.name === content.name && record.id !== content.id
      )
    : [];
  // Lo que ya decidio otro registro no se ofrece: sin esto, editar A le copiaria
  // las sanciones de B. Lo que este registro ya tiene se queda, aunque se repita.
  const resolvedKeys = getResolvedProposalKeys(otherRecords).filter(
    (key) => !savedStates[key]
  );

  // Initial States
  const [isLoading, setIsLoading] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [triedToSave, setTriedToSave] = useState(false);
  const [selectedActions, setSelectedActions] = useState(
    () => splitByOrigin(savedActions).own
  );
  const [addComment, setAddComment] = useState(content.comment || false);
  const [comment, setComment] = useState(content.comment || "");
  const [another, setAnother] = useState(() =>
    splitByOrigin(savedAnother)
      .own.map((item) => item.text)
      .filter(Boolean)
  );
  const [determinationDate, setDeterminationDate] = useState(
    content.determination_date ? dayjs(content.determination_date) : null
    );
  const [executionDate, setExecutionDate] = useState(
    content.execution_date ? dayjs(content.execution_date) : null
  );
  useEffect(() => {
    setDeterminationDate(
      content.determination_date ? dayjs(content.determination_date) : null
    );
    setExecutionDate(
      content.execution_date ? dayjs(content.execution_date) : null
    );
  }, [content]);
  // Dinamic titles & colors
  const titlesType = {
    sanctions: {
      title: t("Resolution.edit_sanctions"),
      inputLabel: t("Resolution.sanctions"),
      inputPlaceholder: t("Resolution.select_one_more_sanctions"),
      addAnotherAction: t("Resolution.add_another_sanction"),
      addActionInput: t("Resolution.another_sanction"),
      addOtherActionPlaceholder: t("Resolution.specify_another_sanction_type"),
      color: "orange",
      colorHover: "orangeII",
      commentPlaceholder: t(
        "Resolution.provide_details_sanction_assigned_person_involved"
      ),
    },
    benefits: {
      title: t("Resolution.edit_compensations"),
      inputLabel: t("Resolution.benefits"),
      inputPlaceholder: t("Resolution.select_one_more_benefits"),
      addAnotherAction: t("Resolution.add_another_compensation"),
      addActionInput: t("Resolution.another_compensation"),
      addOtherActionPlaceholder: t("Resolution.specify_another_benefit_type"),
      color: "vividViolet",
      colorHover: "vividVioletII",
      commentPlaceholder: t(
        "Resolution.provide_details_compensations_awarded_person_involved"
      ),
    },
  };
  // Llave con la que el backend guarda el texto libre de cada tipo.
  const property = isSanctions ? "another_sanction" : "another_benefit";

  // Opciones planas de respaldo cuando el tenant no tiene catalogo. Si la
  // accion guardada no esta en la lista se agrega para que siga visible.
  const fallbackOptions = (() => {
    const base = isSanctions ? sanctionsOptions : benefitOptions;
    const missing = selectedActions
      .map(getActionPath)
      .filter((path) => path && !base.some((option) => option.value === path))
      .map((path) => ({ value: path, label: path }));
    return [...base, ...missing];
  })();

  // Lo que se toma de la investigacion y lo que el resolutor decidio no aplicar
  const { confirmed, another: proposalAnother, discarded } =
    splitProposals(proposals);

  // Un descarte sin motivo deja el registro sin explicar por que no se aplico.
  const hasMissingReason = discarded.some((item) => !item.reason);

  // Validate data
  const cleanAnother = another.map((text) => text.trim()).filter(Boolean);
  const isInvalid =
    (confirmed.length === 0 &&
      proposalAnother.length === 0 &&
      selectedActions.length === 0 &&
      cleanAnother.length === 0) ||
    another.some((text) => text.trim() === "") ||
    hasMissingReason;

  const alreadyRegistered = findAlreadyRegistered({
    records: otherRecords,
    actionsKey: type,
    actions: [...confirmed, ...selectedActions],
    another: [...proposalAnother.map((item) => item.text), ...cleanAnother],
  });

  // Send info
  const onSubmitActions = async () => {
    setTriedToSave(true);
    if (hasMissingReason) {
      enqueueSnackbar(t("Resolution.discard_reason_required"), {
        variant: "error",
      });
      return;
    }
    setIsLoading(true);
    const sending = enqueueSnackbar(`${t("Resolution.sending")}...`, {
      variant: "info",
      persist: true,
    });

    const API_CALLS = {
      sanctions: updateSanctions,
      benefits: updateCompensations,
    };

    const endpoint = API_CALLS[type] || null;

    // Lo confirmado de la investigacion vuelve a guardarse junto a lo propio:
    // para el registro es una sola lista.
    const allActions = [...confirmed, ...selectedActions];
    const allAnother = [
      ...proposalAnother,
      ...cleanAnother.map((text) => ({ text })),
    ];

    const dataToSend = {
      id: content.id,
      report_identifier: id,
      // Siempre se manda: si se omite, djaguar conserva lo que tenia y quitar la
      // ultima accion no surte efecto.
      [type]: allActions,
      // `another` solo si hay algo que escribir o borrar: el PATCH falla en los
      // registros muy viejos, que no traen `specifications`.
      ...((allAnother.length > 0 || savedAnother.length > 0) && {
        another: allAnother.map((item) => ({
          [property]: item.text,
          ...(item.from_investigation && {
            from_investigation: item.from_investigation,
          }),
        })),
      }),
      ...(isSanctions && { discarded }),
      comment,
      determination_date: determinationDate ? determinationDate?.format("YYYY-MM-DD") : null,
      execution_date: executionDate ? executionDate?.format("YYYY-MM-DD") : null,
    };

    if (!endpoint) {
      console.error("No API function available for type:", type);
      closeSnackbar(sending);
      setIsLoading(false);
      return;
    }

    try {
      await endpoint(dataToSend).then((resp) => {
        if (resp.status === 201 || 200) {
          setSelectedActions([]);
          setAddComment(false);
          setComment("");
          setAnother([]);
          setDeterminationDate(null);
          setExecutionDate(null);
          reloadData();
          reportRender();
          setIsLoading(false);
          closeSnackbar(sending);
          enqueueSnackbar(`${t("Resolution.data_successfully_updated")}`, {
            variant: "success",
          });
          setIsLoading(false);
          hdlCloseDialog();
        }
      });
    } catch (error) {
      console.error(error);
      closeSnackbar(sending);
      setIsLoading(false);
    }
  };

  return (
    <>
      <EGIconButton
        iconName="cancel_circle"
        iconColor="primary"
        onClick={hdlCloseDialog}
        sx={{ position: "absolute", top: 15, right: 12 }}
        iconSize={25}
      />
      <DialogTitle
        align="center"
        component="h3"
        color="secondary"
        sx={{ padding: 0 }}
      >
        {titlesType[type].title}
      </DialogTitle>
      <ListItem dense sx={{ padding: 0 }} className="border-bottom">
        <ListItemAvatar>
          <Avatar sx={{ backgroundColor: "transparent" }}>
            <EGIconButton
              iconName={
                content.confirm
                  ? "outline_profile_tick"
                  : "outline_profile_remove"
              }
              color={content.confirm ? titlesType[type].color : "blueGray500"}
              title={
                content.confirm
                  ? t("Resolution.confirmed")
                  : t("Resolution.unconfirmed")
              }
              colorHover={
                content.confirm ? titlesType[type].colorHover : "blueGray700"
              }
            />
          </Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={
            <Box
              display="flex"
              alignItems="center"
              flexWrap="wrap"
              maxWidth={"100%"}
            >
              <Typography
                mr={0.5}
                color="secondary"
                fontWeight={600}
                variant="body1"
                component="span"
              >
                {`${involvedType[content.kind]}: `}
              </Typography>
              <Typography
                color={titlesType[type].color}
                variant="body1"
                component="span"
                fontWeight={700}
              >
                {content.name}
              </Typography>
            </Box>
          }
          secondary={
            <Stack
              spacing={2}
              display="flex"
              alignItems="center"
              justifyContent="normal"
              flexDirection="row"
              flexWrap="wrap"
              py={0.5}
              component={"span"}
            >
              <Typography variant="body2" component="span">
                {involvedType[content.kind || content.rol]}
              </Typography>
              {content.additional && (
                <Typography variant="body2" component="span">{`${t(
                  "Resolution.employee_number"
                )}: ${content.additional}`}</Typography>
              )}
              {content.relationship && (
                <Typography variant="body2" component="span">{`${t(
                  "Resolution.job_position"
                )}: ${content.relationship}`}</Typography>
              )}
              {content.details && (
                <Typography variant="body2" component="span">{`${t(
                  "Resolution.area"
                )}: ${content.details}`}</Typography>
              )}
            </Stack>
          }
        />
      </ListItem>

      <DialogContent sx={{ px: 0, py: 0.5 }}>
        {/* Mismo orden que el alta: fechas, propuestas de las investigaciones y
            al final lo que agrega el resolutor por su cuenta. */}
        {/* Una fecha por renglon: en columnas los labels se cortan. */}
        <Stack spacing={1.5} mt={1.5}>
          <EGDatePicker
            label={`${t("Resolution.determination_date")}:`}
            value={determinationDate}
            onChange={(newValue) => setDeterminationDate(newValue)}
          />
          <EGDatePicker
            label={`${t("Resolution.execution_date")}:`}
            value={executionDate}
            onChange={(newValue) => setExecutionDate(newValue)}
          />
        </Stack>

        {isSanctions && (
          <Box sx={{ mt: 1.5 }}>
            <InvestigationProposals
              investigations={investigationList}
              type={ACTION_TYPES.SANCTION}
              involvedName={content.name}
              proposals={proposals}
              onProposalsChange={setProposals}
              showErrors={triedToSave}
              savedStates={savedStates}
              allowPending={multipleEnabled}
              // Editar nunca aplica algo que nadie eligio en este registro.
              defaultConfirmed={multipleEnabled ? null : true}
              resolvedKeys={resolvedKeys}
            />
          </Box>
        )}

        <Typography
          variant="body2"
          fontWeight={600}
          color="secondary"
          component="div"
          mt={2}
        >
          {t("Resolution.add_on_your_own")}
        </Typography>

        {!isCatalogueLoading && (
          <Box sx={{ mt: 1 }}>
            <CatalogueMultiSelect
              label={`${titlesType[type].inputLabel}:`}
              placeholder={titlesType[type].inputPlaceholder}
              catalogue={catalogue}
              isOwn={isOwn}
              rootPath={rootPath}
              options={fallbackOptions}
              value={selectedActions}
              onChange={setSelectedActions}
              another={another}
              onAnotherChange={setAnother}
              freeTextLabel={titlesType[type].addActionInput}
              freeTextPlaceholder={titlesType[type].addOtherActionPlaceholder}
              addFreeTextTitle={titlesType[type].addAnotherAction}
            />
            {alreadyRegistered.length > 0 && (
              <Typography fontSize={12} color="warning.main" mt={0.5}>
                {t("Resolution.already_registered_actions", {
                  actions: alreadyRegistered.join(", "),
                })}
              </Typography>
            )}
          </Box>
        )}

        {/* Add comment?  */}
        <Stack
          display="flex"
          alignItems="center"
          justifyContent="end"
          flexDirection="row"
          my={0.5}
          spacing={2}
        >
          {(selectedActions.length > 0 ||
            another.length > 0 ||
            proposals.length > 0) &&
            !addComment && (
              <Link onClick={() => setAddComment(true)} underline="hover">
                + {t("Care.add_comment")}
              </Link>
            )}
        </Stack>
        {addComment && (
          <EGInput
            label={`${t("Resolution.details")}: ${comment.length}/500`}
            placeholder={titlesType[type].commentPlaceholder}
            sx={{ mt: 0.5 }}
            multiline
            rows={3}
            value={comment}
            autoFocus
            onChange={(e) => setComment(e.target.value)}
            inputProps={{ maxLength: 500 }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 1, mt: 0.5 }}>
        <EGButton
          label={t("Resolution.save")}
          onClick={onSubmitActions}
          loading={isLoading}
          iconName={isInvalid ? "mark_check" : "mark_check_bold"}
          disabled={isInvalid}
          color={isInvalid ? "light" : "success"}
          iconColor={isInvalid ? "" : "white"}
          iconColorHover={isInvalid ? "" : "white"}
          labelColor={isInvalid ? "" : "white"}
        />
        <EGButton
          iconName="close_icon"
          iconSize={21}
          label={t("Resolution.cancel")}
          onClick={hdlCloseDialog}
          loading={isLoading}
        />
      </DialogActions>
    </>
  );
};
